/**
 * PATH: packages/email-hub/src/feature/components/IntegrationEventsPanel.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-INTEGRATIONEVENTSPANEL-001
 * PURPOSE: Canonical body of this email component. Lifted from
 *          apps/<tenant>/src/features/email/components/IntegrationEventsPanel.tsx
 *          on K3a (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale (BUG-0032 was
 *          the most recent example). One canonical body, both
 *          tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/components/IntegrationEventsPanel.tsx:
 *     thin re-export shim that points back here.
 */

import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, AlertTriangle, ChevronDown, Copy, Info, Loader2, RefreshCw, Trash2, X } from 'lucide-react'
import {
  useIntegrationEvents,
  type IntegrationEvent,
  type IntegrationEventSeverity,
} from '../../_internal/services/integrationEvents'
import ShadowResolutionStats from './ShadowResolutionStats'
import DspyArtifacts from './DspyArtifacts'

interface Props {
  open: boolean
  onClose: () => void
}

// Light-mode-first colours with explicit dark-mode overrides. The
// previous palette used dark-mode-only `text-red-300` / `text-amber-200`
// which on the user's white panel turned into illegible washed-out
// pastels (the screenshot the user pasted is exactly that bug).
const SEVERITY_TONES: Record<IntegrationEventSeverity, string> = {
  error:
    'border-red-500/50 bg-red-500/10 text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200',
  warning:
    'border-amber-500/60 bg-amber-500/15 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100',
  info:
    'border-border bg-muted text-foreground dark:border-slate-500/30 dark:bg-slate-500/5 dark:text-slate-200',
}

/** Inline reason chip styling — high-contrast in both modes. */
const REASON_CHIP =
  'font-mono text-[11px] px-1.5 py-0.5 rounded bg-foreground/10 dark:bg-white/10 border border-border/60'

const SEVERITY_ICON: Record<IntegrationEventSeverity, typeof AlertCircle> = {
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'unknown'
  const ts = Date.parse(iso)
  if (Number.isNaN(ts)) return iso
  const seconds = Math.max(0, Math.floor((Date.now() - ts) / 1000))
  if (seconds < 30) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return new Date(ts).toLocaleString()
}

function summariseEvent(e: IntegrationEvent): string {
  const lines = [
    `[${e.severity.toUpperCase()}] ${e.source}${e.reason ? ` (${e.reason})` : ''}`,
    e.message,
    `time: ${e.created_at ?? '?'}  request_id: ${e.request_id ?? '-'}  user_id: ${e.user_id ?? '-'}  event_id: ${e.id}`,
  ]
  if (e.context && Object.keys(e.context).length) {
    lines.push(`context: ${JSON.stringify(e.context)}`)
  }
  if (e.detail) {
    lines.push(`detail: ${e.detail}`)
  }
  return lines.join('\n')
}

function EventRow({ event, onCopy }: { event: IntegrationEvent; onCopy: (text: string) => void }) {
  const [expanded, setExpanded] = useState(false)
  const Icon = SEVERITY_ICON[event.severity] ?? Info
  const tone = SEVERITY_TONES[event.severity] ?? SEVERITY_TONES.info
  const hasDetail = !!(event.detail || (event.context && Object.keys(event.context).length))

  return (
    <li className={`border rounded-md p-2.5 ${tone}`}>
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="font-mono text-[11px] font-medium">{event.source}</span>
            {event.reason && (
              <span className={REASON_CHIP}>{event.reason}</span>
            )}
            <span className="ml-auto text-[11px] font-medium">
              {relativeTime(event.created_at)}
            </span>
          </div>
          <div className="text-sm mt-1 break-words font-medium leading-snug">
            {event.message}
          </div>
          {hasDetail && (
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium hover:underline"
            >
              <ChevronDown
                className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
              {expanded ? 'Hide details' : 'Show details'}
            </button>
          )}
          {expanded && hasDetail && (
            <pre className="mt-1.5 text-[11px] bg-foreground/5 dark:bg-white/10 rounded p-2 whitespace-pre-wrap break-words font-mono">
              {event.context && Object.keys(event.context).length > 0 && (
                <>{JSON.stringify(event.context, null, 2)}{'\n'}</>
              )}
              {event.detail || ''}
            </pre>
          )}
        </div>
        <button
          type="button"
          onClick={() => onCopy(summariseEvent(event))}
          className="opacity-60 hover:opacity-100 transition-opacity"
          title="Copy this event"
          aria-label="Copy event to clipboard"
        >
          <Copy className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  )
}

export default function IntegrationEventsPanel({ open, onClose }: Props) {
  const [filter, setFilter] = useState<'all' | IntegrationEventSeverity>('all')
  const { events, loading, error, refresh, errorCount, warningCount, clear, clearing, authFailed } = useIntegrationEvents({
    severity: filter === 'all' ? undefined : filter,
    sourcePrefix: undefined,
    limit: 100,
    pollMs: open ? 30_000 : 0,
  })
  const [copyMsg, setCopyMsg] = useState<string | null>(null)
  const [confirmingClear, setConfirmingClear] = useState(false)

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // On open, force one refresh so a repaired session recovers, and so
  // the drawer hydrates from the network when the shared cache is empty.
  // When auth is latched, force still attempts recovery; last-good rows
  // remain visible from the shared cache either way.
  useEffect(() => {
    if (!open) return
    void refresh({ force: true })
  }, [open, refresh])

  const visible = useMemo(() => events, [events])

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopyMsg('Copied')
    } catch {
      setCopyMsg('Copy failed')
    }
    window.setTimeout(() => setCopyMsg(null), 1500)
  }

  const handleCopyAll = async () => {
    if (!visible.length) return
    const all = visible.map(summariseEvent).join('\n\n---\n\n')
    await handleCopy(all)
  }

  // Two-step clear: first click arms the confirm state (button turns
  // red and label changes to "Confirm clear"), second click within 5s
  // actually fires the DELETE. Anywhere else cancels. Keeps clear out
  // of one-tap-misclick territory without a separate modal.
  useEffect(() => {
    if (!confirmingClear) return
    const t = window.setTimeout(() => setConfirmingClear(false), 5_000)
    return () => window.clearTimeout(t)
  }, [confirmingClear])

  const handleClearClick = async () => {
    if (!confirmingClear) {
      setConfirmingClear(true)
      return
    }
    setConfirmingClear(false)
    // Cap the clear at the highest id we currently see — anything that
    // arrived after the user opened the panel is preserved.
    const beforeId = visible.length ? Math.max(...visible.map(e => e.id)) : undefined
    try {
      const n = await clear(beforeId)
      setCopyMsg(`Cleared ${n}`)
      window.setTimeout(() => setCopyMsg(null), 1500)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Clear failed'
      setCopyMsg(msg)
      window.setTimeout(() => setCopyMsg(null), 2500)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Recent issues"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-label="Close"
        tabIndex={-1}
      />
      <aside className="relative w-full max-w-md h-full bg-background border-l border-border shadow-xl flex flex-col">
        <header className="px-4 py-3 border-b border-border flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold">Recent issues</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {errorCount > 0
                ? `${errorCount} error${errorCount === 1 ? '' : 's'}`
                : warningCount > 0
                  ? `${warningCount} warning${warningCount === 1 ? '' : 's'}`
                  : visible.length > 0
                    ? `${visible.length} event${visible.length === 1 ? '' : 's'}`
                    : 'No issues recorded'}
              {authFailed ? ' · auto-refresh paused' : ' · refreshes every 30s'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void refresh({ force: true })}
            disabled={loading}
            className="text-xs px-2 py-1 rounded border border-border hover:bg-accent transition-colors flex items-center gap-1 disabled:opacity-50"
            title={authFailed ? 'Retry - session may have been repaired' : 'Refresh now'}
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Refresh
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close panel"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="px-4 py-2 border-b border-border flex items-center gap-2 text-xs">
          {(['all', 'error', 'warning', 'info'] as const).map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => setFilter(opt)}
              className={`px-2 py-1 rounded border transition-colors ${
                filter === opt
                  ? 'border-foreground bg-foreground/10'
                  : 'border-border opacity-70 hover:opacity-100'
              }`}
            >
              {opt[0].toUpperCase() + opt.slice(1)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => void handleCopyAll()}
            disabled={!visible.length}
            className="ml-auto text-xs px-2 py-1 rounded border border-border hover:bg-accent transition-colors flex items-center gap-1 disabled:opacity-40"
            title="Copy every visible event to the clipboard"
          >
            <Copy className="h-3 w-3" />
            {copyMsg ?? 'Copy all'}
          </button>
          <button
            type="button"
            onClick={() => void handleClearClick()}
            disabled={!visible.length || clearing}
            className={`text-xs px-2 py-1 rounded border transition-colors flex items-center gap-1 disabled:opacity-40 ${
              confirmingClear
                ? 'border-red-500 bg-red-500/15 text-red-700 dark:text-red-300 hover:bg-red-500/25'
                : 'border-border hover:bg-accent'
            }`}
            title={
              confirmingClear
                ? 'Click again to confirm - clears every event currently shown for this organisation'
                : 'Clear all events shown'
            }
          >
            {clearing ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
            {confirmingClear ? 'Confirm clear' : 'Clear all'}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
          {/* Phase 4 — AI shadow-resolution stats (hidden until the org
              has accumulated shadow EmailEntityLink rows). Lazy: only
              fetches while the drawer is open. */}
          <ShadowResolutionStats open={open} />
          {/* Phase 3 — compiled DSPy classifier artifacts + promote/rollback
              (hidden until the nightly compile has produced an artifact). */}
          <DspyArtifacts open={open} />
          {authFailed && (
            // BUG-0038: dedicated state — the endpoint is reachable but this
            // session's sign-in was rejected for it. Change #294 rewrote this
            // copy: it used to claim the operator's ACCOUNT lacked read access,
            // which the backend cannot produce (IntegrationEventsView carries
            // IsAuthenticated and no permission check at all), and it sent an
            // org owner hunting a permissions bug that did not exist. Say the
            // one thing that is actually true of a surviving 401 here, and
            // point at the recovery path.
            <div className="text-xs text-amber-700 dark:text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-2">
              Recent issues are paused - this session's sign-in was rejected
              when reading the log, and the automatic retry did not recover it.
              Nothing else in the app is affected, and this is not a permission
              on your account. Try Refresh, or reload the page to re-establish
              the session.
            </div>
          )}
          {!authFailed && error && (
            <div className="text-xs text-red-400 px-1 py-2">
              Couldn't load events: {error}
            </div>
          )}
          {!authFailed && !error && visible.length === 0 && !loading && (
            <p className="text-xs text-muted-foreground px-1 py-2">
              Nothing recorded for this organisation yet. Failures from the email
              module (send, sync, autocomplete, Outlook status) will appear here.
            </p>
          )}
          <ul className="space-y-2">
            {visible.map(e => (
              <EventRow key={e.id} event={e} onCopy={handleCopy} />
            ))}
          </ul>
        </div>
      </aside>
    </div>
  )
}
